import React, { useCallback, useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Icon, List } from "semantic-ui-react";
import { useTextResources } from "../../../../language";
import { AddReasonCodesPopup } from "./AddReasonCodesPopup";
import { PriceListContext } from "../../context";
import { StaticDiscounts } from '../../../../api/services/next';

export const ReasonCodes = ({ shouldAddMappings, setShouldAddMappings }) => {
  const text = useTextResources();
  const { state, setState, setDirtyState } = useContext(PriceListContext);
  const {
    Id: rateId = undefined,
    StaticDiscountMappings: staticDiscountMappings = [],
  } = state;

  const [staticDiscountMappingsListItems, setStaticDiscountMappingsListItems] = useState([]);
  const [isAddReasonCodesPopupOpen, setIsAddReasonCodesPopupOpen] = useState(false);

  const openAddReasonCodesPopup = useCallback(() => setIsAddReasonCodesPopupOpen(true));
  const hideAddReasonCodesPopup = useCallback(() => setIsAddReasonCodesPopupOpen(false));

  const updateContextState = useCallback((fieldName, updateFn) => {
    setState((prevState) => ({ ...prevState, [fieldName]: updateFn(prevState[fieldName]) }));
    setDirtyState(prevState => ({ ...prevState, GeneralTab: true }));
  }, [setState, setDirtyState]);

  const handleRemoveStaticDiscountMapping = (staticDiscountMappingToRemove) => {
    updateContextState("StaticDiscountMappings", (prevStaticDiscountMappings) => {
      return staticDiscountMappingToRemove.Id !== 0
        ? prevStaticDiscountMappings.map((staticDiscountMapping) => (staticDiscountMapping === staticDiscountMappingToRemove ? { ...staticDiscountMapping, Id: -staticDiscountMapping.Id } : staticDiscountMapping))
        : prevStaticDiscountMappings.filter((staticDiscountMapping) => (staticDiscountMapping !== staticDiscountMappingToRemove))
    });
  }

  useEffect(() => {
    const listItems = staticDiscountMappings
      .filter(staticDiscountMapping => staticDiscountMapping.Id >= 0)
      .map((staticDiscountMapping) => ({
        staticDiscountMapping
      }));

    setStaticDiscountMappingsListItems(listItems);
  }, [staticDiscountMappings, updateContextState]);

  useEffect(() => {
    (async () => {
      try {
        if (shouldAddMappings) {
          const staticDiscounts = await StaticDiscounts.GetStaticDiscounts();
          const existingDiscountsIds = staticDiscountMappingsListItems.map(m => m.staticDiscountMapping.StaticDiscountId);
          const newStaticDiscountMappings = staticDiscounts.filter(sd => !existingDiscountsIds.includes(sd.Id)).map(sd => {
            const newStaticDiscountMappingWithDescription = {
              Id: 0,
              StaticDiscountId: sd.Id,
              StaticDiscount: sd,
              Discount: sd.Discount,
              EntityId: rateId,
            }

            return newStaticDiscountMappingWithDescription;
          });
          updateContextState("StaticDiscountMappings", (prev) => [...prev, ...newStaticDiscountMappings]);
          setShouldAddMappings(false);
        }
      } catch (error) {
        console.log(error)
      }
    })()
  }, [shouldAddMappings])

  const handleAddStaticDiscountMapping = (newStaticDiscountMapping) => {
    updateContextState("StaticDiscountMappings", (prev) => [...prev, newStaticDiscountMapping]);
  }

  return (
    <div>
      <div>
        <a
          style={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={openAddReasonCodesPopup}
        >
          {text("AddReasonCodes")}
        </a>
        <span style={{ marginLeft: 20 }} onClick={openAddReasonCodesPopup}><Icon link name='pencil' /></span>
        <AddReasonCodesPopup
          RateId={rateId}
          open={isAddReasonCodesPopupOpen}
          onClose={hideAddReasonCodesPopup}
          onAdd={handleAddStaticDiscountMapping}
          staticDiscountMappings={staticDiscountMappings}
        />
      </div>
      <div style={{ maxHeight: 160, overflow: "auto", marginTop: 5 }}>
        <List divided>
          {staticDiscountMappingsListItems.map((item) => (
            <StaticDiscountMappingListItem
              key={item.staticDiscountMapping.StaticDiscountId}
              {...item}
              onRemove={handleRemoveStaticDiscountMapping}
            />)
          )}
        </List>
      </div>
    </div>
  );
}

const StaticDiscountMappingListItem = ({
  staticDiscountMapping,
  onRemove,
}) => {
  const handleRemove = useCallback(() => onRemove(staticDiscountMapping), [onRemove]);

  return (
    <List.Item key={staticDiscountMapping.Id} style={{ minHeight: 18 }}>
      <List.Content floated="right">
        <Icon
          name="delete"
          link
          onClick={handleRemove}
        />
      </List.Content>
      <List.Header>
        {`${staticDiscountMapping.StaticDiscount?.Description} (${staticDiscountMapping.StaticDiscount?.Code})${staticDiscountMapping.Discount === undefined ? '' : ' - ' + staticDiscountMapping.Discount + '%'}`}
      </List.Header>
    </List.Item>
  )
}

StaticDiscountMappingListItem.propTypes = {
  staticDiscountMapping: PropTypes.shape({
    StaticDiscount: PropTypes.shape({
      Id: PropTypes.number.isRequired,
      Code: PropTypes.string.isRequired,
      Description: PropTypes.string,
    }),
    Discount: PropTypes.number,
    Id: PropTypes.number.isRequired,
  }),
  onRemove: PropTypes.func.isRequired,
};

ReasonCodes.propTypes = {
  shouldAddMappings: PropTypes.bool.isRequired,
  setShouldAddMappings: PropTypes.func.isRequired
};
